import { Component, OnInit, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LLMService } from 'app/services/llm.service';
import { LLMError } from 'app/models/llm.models';
import { environment } from 'environments/environment';

@Component({
  selector: 'llm-actions',
  templateUrl: './llm-actions.component.html',
  standalone: false,
})
export class LLMActionsComponent implements OnInit {
  isLoadingCopy = false;
  isLoadingView = false;
  isCopied = false;
  message = '';
  messageType: 'success' | 'error' | 'alert' | 'info' = 'info';
  showMessage = false;

  llmFilePath: string | null = null;
  markdownFilePath: string | null = null;
  @Input() isCopyForLLMButton = true;
  @Input() isViewAsMarkdownButton = true;

  constructor(private llmService: LLMService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.buildFilePaths();
  }

  private buildFilePaths(): void {
    // Uncomment when we start accepting file paths as input and want to validate them
    // if (this.llmFilePath || this.markdownFilePath) {
    //   if (this.llmFilePath) {
    //     this.llmFilePath = this.validateAndCleanPath(this.llmFilePath);
    //   }
    //   if (this.markdownFilePath) {
    //     this.markdownFilePath = this.validateAndCleanPath(this.markdownFilePath);
    //   }

    //   if (this.llmFilePath && this.markdownFilePath && this.llmFilePath !== this.markdownFilePath) {
    //     this.showErrorMessage('LLM File Path and Markdown File Path must point to the same path.');
    //     this.llmFilePath = null;
    //     this.markdownFilePath = null;
    //   }
    //   return;
    // }

    const currentUrl = this.route.snapshot.url.map((segment) => segment.path).join('/');

    if (currentUrl) {
      const filePath = `${currentUrl}/index.md`;
      if (!this.llmFilePath) {
        this.llmFilePath = filePath;
      }
      if (!this.markdownFilePath) {
        this.markdownFilePath = filePath;
      }
    }
  }

  private validateAndCleanPath(path: string): string | null {
    if (!path) {
      this.showErrorMessage('Path is empty or null');
      return null;
    }
    if (path.startsWith('http://') || path.startsWith('https://')) {
      this.showErrorMessage('External URLs are not allowed. Path must be a relative file path.');
      return null;
    }
    const cleanPath = path.trim().replace(/^\/+|\/+$/g, '');
    if (cleanPath.includes('..') || cleanPath.includes('//')) {
      this.showErrorMessage('Invalid path: Path traversal is not allowed.');
      return null;
    }
    return cleanPath;
  }

  copyLLMContent(): void {
    if (!this.llmFilePath) {
      this.showErrorMessage('File path not configured');
      return;
    }

    this.isLoadingCopy = true;

    this.llmService.copyLLMContent(this.llmFilePath).subscribe({
      next: () => {
        this.handleCopySuccess();
        this.isLoadingCopy = false;
      },
      error: (error: LLMError) => {
        this.handleError(error);
        this.isLoadingCopy = false;
      },
    });
  }

  viewMarkdown(): void {
    if (!this.markdownFilePath) {
      this.showErrorMessage('File path not configured');
      return;
    }

    this.isLoadingView = true;

    try {
      const fullUrl = `${environment.appBaseUrl}/${this.markdownFilePath}`;
      const newTab = window.open(fullUrl, '_blank');

      if (!newTab) {
        this.showErrorMessage('Could not open tab. Please check popup blocker settings.');
        this.isLoadingView = false;
        return;
      }
      this.isLoadingView = false;
    } catch (error) {
      this.showErrorMessage(`Failed to open markdown: ${error instanceof Error ? error.message : String(error)}`);
      this.isLoadingView = false;
    }
  }

  private handleCopySuccess(): void {
    const message = `Copied to clipboard`;
    this.showSuccessMessage(message);

    this.isCopied = true;
    setTimeout(() => {
      this.isCopied = false;
    }, 2000);
  }

  private handleError(error: LLMError): void {
    const message = this.getErrorMessage(error.code, error.message);
    this.showErrorMessage(message);
  }

  private getErrorMessage(code: string, message: string): string {
    const errorMessages: { [key: string]: string } = {
      INVALID_PATH: 'File path configuration is invalid',
      FILE_NOT_FOUND: "LLM file not found. The file may not exist or hasn't been created yet.",
      ACCESS_DENIED: 'Access denied. You may not have permission to access this file.',
      REQUEST_TIMEOUT: 'Request timeout. The server took too long to respond. Please try again.',
      UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
    };

    return errorMessages[code] || `Error: ${message}`;
  }

  private showSuccessMessage(message: string): void {
    this.message = message;
    this.messageType = 'success';
    this.showMessage = true;

    setTimeout(() => {
      this.showMessage = false;
    }, 2000);
  }

  private showErrorMessage(message: string): void {
    this.message = message;
    this.messageType = 'error';
    this.showMessage = true;

    setTimeout(() => {
      this.showMessage = false;
    }, 2000);
  }

  dismissMessage(): void {
    this.showMessage = false;
  }

  getNotificationTone(): string {
    const classMap: { [key: string]: string } = {
      success: 'success',
      error: 'error',
      info: 'info',
      alert: 'alert',
    };
    return classMap[this.messageType] || 'info';
  }

  getNotificationTitle(): string {
    const titleMap: { [key: string]: string } = {
      success: 'Success',
      error: 'Error',
      info: 'Information',
      alert: 'Important',
    };
    return titleMap[this.messageType] || 'Information';
  }
}
